import { createSlice } from "@reduxjs/toolkit";
import { SALE_INVOICE_DEFAULT_COLUMNS } from "../../app/saleInvoice/layout/constant";
import { sortAllView } from "../utils";
import { boardViewForAllocation } from "./TransferOrderSliceV2";

const billDefaultColumns = [
  "BILL NO",
  "SUPPLIER",
  "BILL DATE",
  "DUE DATE",
  "SUPPLIER REFERENCE",
  "PURCHASE ORDERS",
  "TRANSFER ORDERS",
  "RECEIPTS",
  "SUB TOTAL",
  "TAX",
  "TOTAL",
  "CURRENCY",
  "STATUS",
];

const supplierCreditNoteDefaultColumns = [
  "CREDIT NO",
  "SUPPLIER",
  "CREDIT DATE",
  "SUPPLIER REFERENCE",
  "TAX EXCLUDED",
  "TOTAL",
  "BILL CURRENCY",
  "TOTAL IN CURRENCY",
  "PAYMENT STATUS",
];

const customerCreditNoteDefaultColumns = [
  "CREDIT NO",
  "CUSTOMER",
  "CREDIT DATE",
  "REFERENCE",
  "SALE INVOICE",
  "SUB TOTAL",
  "TAX",
  "TOTAL",
  "CURRENCY",
  "STATUS",
];

const prePaymentDefaultColumns = [
  "PAYMENT NO",
  "SUPPLIER",
  "REFERENCE",
  "PAYMENT TYPE",
  "PURCHASE ORDERS",
  "ACCOUNT",
  "BANK ACCOUNT",
  "CURRENCY",
  "DATE PAID",
  "AMOUNT",
  "CREATED BY",
  "CREATE DATE",
  "STATUS",
];

const billChargeTypeDefaultColumns = [
  "NAME",
  "AMOUNT TYPE",
  "DISTRIBUTION TYPE",
  "UNIT PRICE",
  "IS LANDED COST",
  "DESCRIPTION",
  "CREATED BY",
  "DATE OF CREATION",
];

const initialState = {
  billChargeTypes: { count: 0, results: [] },
  billChargeTypesParams: { limit: 10, offset: 0 },
  billChargeTypeLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: billChargeTypeDefaultColumns,
      allViews: {
        "Default View": billChargeTypeDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  bills: { count: 0, results: [] },
  billParams: { limit: 10, offset: 0 },
  bill: null,
  billsSelection: [],

  supplierCreditNotes: { count: 0, results: [] },
  supplierCreditNoteParams: { limit: 10, offset: 0 },
  supplierCreditNote: null,
  supplierCreditNotesSelection: [],

  customerCreditNotes: { count: 0, results: [] },
  customerCreditNoteParams: { limit: 10, offset: 0 },
  customerCreditNote: null,
  customerCreditNotesSelection: [],

  prePayments: { count: 0, results: [] },
  prePaymentParams: { limit: 10, offset: 0 },
  prePaymentsForPODetail: [],
  prePaymentSelection: [],

  saleInvoices: { count: 0, results: [] },
  saleInvoiceParams: { limit: 10, offset: 0 },
  saleInvoice: null,
  saleInvoiceSelection: [],

  billLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: billDefaultColumns,
      allViews: {
        "Default View": billDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  saleInvoiceLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: SALE_INVOICE_DEFAULT_COLUMNS,
      allViews: {
        "Default View": SALE_INVOICE_DEFAULT_COLUMNS,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  supplierCreditNoteLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: supplierCreditNoteDefaultColumns,
      allViews: {
        "Default View": supplierCreditNoteDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  customerCreditNoteLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: customerCreditNoteDefaultColumns,
      allViews: {
        "Default View": customerCreditNoteDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  prePaymentLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: prePaymentDefaultColumns,
      allViews: {
        "Default View": prePaymentDefaultColumns,
      },
      nameOfViews: [],
    },
    ...boardViewForAllocation,
  },

  billChargeTypesSelection: [],

  invoices: [],
  invoicesParams: { limit: 10, offset: 0 },

  invoiceIDs: [],

  invoicesHistoryList: { count: 0, results: [] },
  invoicesHistoryParams: { limit: 10, offset: 0 },

  invoicesList: { count: 0, results: [] },

  pendingInvoicesList: { count: 0, results: [] },
  pendingInvoicesParams: { limit: 10, offset: 0 },
};

const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    deleteInvoice: (state, action) => {
      state.invoices = {
        count: state.invoices.count - 1,
        results: state.invoices.results.filter(item => item.id !== action.payload),
      };

      state.pendingInvoicesList = {
        count: state.pendingInvoicesList.count - 1,
        results: state.pendingInvoicesList.results.filter(item => item.id !== action.payload),
      };
    },
    getInvoicesHistoryList: (state, action) => {
      state.invoicesHistoryList = action.payload;
    },
    getInvoicesList: (state, action) => {
      state.invoicesList = action.payload;
    },
    getPendingInvoicesList: (state, action) => {
      state.pendingInvoicesList = action.payload;
    },
    resetBill: state => {
      state.bill = initialState.bill;
    },
    resetBillChargeTypesSelection: state => {
      state.billChargeTypesSelection = initialState.billChargeTypesSelection;
    },
    resetInvoiceIDs: state => {
      state.invoiceIDs = initialState.invoiceIDs;
    },
    resetInvoices: state => {
      state.invoices = initialState.invoices;
    },
    resetPrePayment: state => {
      state.prePayment = initialState.prePayment;
    },
    resetSupplierCreditNoteDetail: state => {
      state.supplierCreditNote = initialState.supplierCreditNote;
    },
    resetCustomerCreditNoteDetail: state => {
      state.customerCreditNote = initialState.customerCreditNote;
    },
    resetInvoicesHistoryParams: state => {
      state.invoicesHistoryParams = { limit: state.invoicesHistoryParams.limit, offset: 0 };
    },
    resetInvoicesParams: state => {
      state.invoicesParams = { limit: state.invoicesParams.limit, offset: 0 };
    },
    resetPendingInvoicesParams: state => {
      state.pendingInvoicesParams = { limit: state.pendingInvoicesParams.limit, offset: 0 };
    },
    setBill: (state, action) => {
      state.bill = action.payload;
    },
    setBills: (state, action) => {
      state.bills = action.payload;
    },
    setBillsSelection: (state, action) => {
      state.billsSelection = action.payload;
    },
    setBillsParams: (state, action) => {
      state.billParams = { ...action.payload };
    },
    resetSaleInvoice: state => {
      state.saleInvoice = initialState.saleInvoice;
    },
    setSaleInvoice: (state, action) => {
      state.saleInvoice = action.payload;
    },
    setSaleInvoices: (state, action) => {
      state.saleInvoices = action.payload;
    },
    setSaleInvoiceSelection: (state, action) => {
      state.saleInvoiceSelection = action.payload;
    },
    setSaleInvoicesParams: (state, action) => {
      state.saleInvoiceParams = { ...action.payload };
    },
    updateSaleInvoicesParams: (state, action) => {
      state.saleInvoiceParams = { ...state.saleInvoiceParams, ...action.payload };
    },
    setSaleInvoiceLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.saleInvoiceLayout = {
        ...state.saleInvoiceLayout,
        listView: {
          ...state.saleInvoiceLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.saleInvoiceLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    updateBillsParams: (state, action) => {
      state.billParams = { ...state.billParams, ...action.payload };
    },
    updatePrePaymentParams: (state, action) => {
      state.prePaymentParams = { ...state.prePaymentParams, ...action.payload };
    },
    updateSupplierCreditNoteParams: (state, action) => {
      state.supplierCreditNoteParams = { ...state.supplierCreditNoteParams, ...action.payload };
    },
    updateCustomerCreditNoteParams: (state, action) => {
      state.customerCreditNoteParams = { ...state.customerCreditNoteParams, ...action.payload };
    },

    setBillLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.billLayout = {
        ...state.billLayout,
        listView: {
          ...state.billLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.billLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setSupplierCreditNoteLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.supplierCreditNoteLayout = {
        ...state.supplierCreditNoteLayout,
        listView: {
          ...state.supplierCreditNoteLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.supplierCreditNoteLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setCustomerCreditNoteLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.customerCreditNoteLayout = {
        ...state.customerCreditNoteLayout,
        listView: {
          ...state.customerCreditNoteLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: {
            ...state.customerCreditNoteLayout.listView.allViews,
            ...views.list.allViews,
          },
        },
        completeListOfViews: action.payload,
      };
    },
    setPrePaymentLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.prePaymentLayout = {
        ...state.prePaymentLayout,
        listView: {
          ...state.prePaymentLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.prePaymentLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setPrePaymentsForPODetail: (state, action) => {
      state.prePaymentsForPODetail = action.payload;
    },
    setPrePayments: (state, action) => {
      state.prePayments = action.payload;
    },
    setPrePaymentParams: (state, action) => {
      state.prePaymentParams = { ...action.payload };
    },
    setPrePaymentSelection: (state, action) => {
      state.prePaymentSelection = action.payload;
    },
    setBillChargeTypesSelection: (state, action) => {
      state.billChargeTypesSelection = action.payload;
    },
    setSupplierCreditNote: (state, action) => {
      state.supplierCreditNote = action.payload;
    },
    setSupplierCreditNotes: (state, action) => {
      state.supplierCreditNotes = action.payload;
    },
    setSupplierCreditNotesSelection: (state, action) => {
      state.supplierCreditNotesSelection = action.payload;
    },
    setCustomerCreditNote: (state, action) => {
      state.customerCreditNote = action.payload;
    },
    setCustomerCreditNotes: (state, action) => {
      state.customerCreditNotes = action.payload;
    },
    setCustomerCreditNotesSelection: (state, action) => {
      state.customerCreditNotesSelection = action.payload;
    },
    setInvoiceIDs: (state, action) => {
      state.invoiceIDs = action.payload;
    },
    setInvoices: (state, action) => {
      state.invoices = action.payload;
    },
    setInvoicesHistoryList: (state, action) => {
      state.invoicesHistoryList = action.payload;
    },
    setInvoicesHistoryParams: (state, action) => {
      state.invoicesHistoryParams = action.payload;
    },
    setInvoicesParams: (state, action) => {
      state.invoicesParams = {
        ...action.payload,
      };
    },
    setPendingInvoicesList: (state, action) => {
      state.pendingInvoicesList = action.payload;
    },
    setPendingInvoicesParams: (state, action) => {
      state.pendingInvoicesParams = {
        ...action.payload,
      };
    },
    updateBillStatus: (state, action) => {
      const {
        id,
        data: { status },
      } = action.payload;
      state.bills.results = state.bills.results.map(item => {
        if (item.id === id) {
          item.status = status;
        }
        return item;
      });
    },
    updateStatusForBill: (state, action) => {
      const {
        data: { status },
      } = action.payload;
      state.bill = { ...state.bill, status };
    },
    updateInvoicesHistoryParams: (state, action) => {
      state.invoicesHistoryParams = { ...state.invoicesHistoryParams, ...action.payload };
    },
    updateInvoicesParams: (state, action) => {
      state.invoicesParams = { ...state.invoicesParams, ...action.payload };
    },
    updatePendingInvoicesParams: (state, action) => {
      state.pendingInvoicesParams = { ...state.pendingInvoicesParams, ...action.payload };
    },
    setBillChargeTypes: (state, action) => {
      state.billChargeTypes = action.payload;
    },
    setBillChargeTypeLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.billChargeTypeLayout = {
        ...state.billChargeTypeLayout,
        listView: {
          ...state.billChargeTypeLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: {
            ...state.billChargeTypeLayout.listView.allViews,
            ...views.list.allViews,
          },
        },
        completeListOfViews: action.payload,
      };
    },
    updateBillChargeTypesParams: (state, action) => {
      state.billChargeTypesParams = { ...state.billChargeTypesParams, ...action.payload };
    },
  },
});

export const {
  deleteInvoice,
  getInvoicesHistoryList,
  getInvoicesList,
  getPendingInvoicesList,
  resetBill,
  resetBillChargeTypesSelection,
  resetInvoiceIDs,
  resetInvoices,
  resetInvoicesHistoryParams,
  resetInvoicesParams,
  resetPendingInvoicesParams,
  resetCustomerCreditNoteDetail,
  resetSupplierCreditNoteDetail,
  setBill,
  setBillChargeTypesSelection,
  setBillChargeTypes,
  setBillChargeTypeLayoutAllViews,
  updateBillChargeTypesParams,
  setSupplierCreditNote,
  setSupplierCreditNotes,
  setSupplierCreditNotesSelection,
  setBills,
  setBillsSelection,
  setBillsParams,
  resetSaleInvoice,
  setSaleInvoice,
  setSaleInvoices,
  setSaleInvoicesParams,
  updateSaleInvoicesParams,
  setSaleInvoiceLayoutAllViews,
  setSaleInvoiceSelection,
  setBillLayoutAllViews,
  setCustomerCreditNote,
  setCustomerCreditNotes,
  setCustomerCreditNotesSelection,
  setCustomerCreditNoteLayoutAllViews,
  setSupplierCreditNoteLayoutAllViews,
  setPrePaymentLayoutAllViews,
  setPrePaymentsForPODetail,
  setPrePayments,
  setPrePaymentParams,
  setPrePaymentSelection,
  resetPrePayment,
  updatePrePaymentParams,
  updateBillsParams,
  setInvoiceIDs,
  setInvoices,
  setInvoicesHistoryList,
  setInvoicesHistoryParams,
  setInvoicesParams,
  setPendingInvoicesList,
  setPendingInvoicesParams,
  updateBillStatus,
  updateStatusForBill,
  updateInvoicesHistoryParams,
  updateInvoicesParams,
  updatePendingInvoicesParams,
  updateCustomerCreditNoteParams,
  updateSupplierCreditNoteParams,
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
